import AppRouter from 'common/AppRouter';
import { LOCAL_STORAGE_KEYS } from 'common/constants/constants';
import { logOut } from 'common/services/auth.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import Loader from 'connected-components/Loader';
import TopBar from 'connected-components/TopBar';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { Route, Switch } from 'react-router-dom';
import { getOrganization as getOrganizationCaller } from 'state/callers';
import { isPostOnboardingWithoutReposSelector } from 'state/selectors/flows';
import ProviderSetupV2 from 'views/RegisterV2/views/ProviderSetupV2/ProviderSetupV2';
import AddOrganization from './AddOrganization';
import AddRepositories from './AddRepositories';
import Processing from './Processing';
import { isTeamLicenseSelector } from 'state/selectors/license';
import AddProvider from './AddProvider';
import VerifyProvider from './VerifyProvider';
import { FEATURE_FLAGS_NAMES } from '@linearb/linjs-common';
import { getAdHocSettingOrDefinitionValue } from 'common/services/settings.service';

/**
 * Onboarding flow component responsible for routing users through setup steps.
 * @param {object} props
 * @param {object} props.account
 * @param {object} props.organization
 * @param {Array<object>} [props.gitSensorAuthorizations]
 * @param {Array<object>} [props.repositories]
 * @param {object} [props.jobs]
 * @returns {JSX.Element}
 */
const Onboarding = props => {
  const {
    account,
    organization,
    gitSensorAuthorizations = [],
    repositories = [],
    jobs = {}
  } = props;
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();
  const [providerOrganizationsVerified, setProviderOrganizationsVerified] =
    useState(location?.state?.providerOrganizationsVerified);

  const getOrganization = () => dispatch(getOrganizationCaller());
  const isTeamLicense = useSelector(isTeamLicenseSelector);

  const isPostOnboardingWithoutRepos = useSelector(
    isPostOnboardingWithoutReposSelector
  );

  useEffect(() => {
    if (location?.state?.providerOrganizationsVerified) {
      setProviderOrganizationsVerified(
        location.state.providerOrganizationsVerified
      );
    }
  }, [location?.state?.providerOrganizationsVerified]);

  useEffect(() => {
    if (account?.organization_id) {
      getOrganization();
    }
  }, [account]);

  useEffect(() => {
    const handleOnboarding = async () => {
      if (!account) {
        return logOut();
      }

      if (!account.organization_id) {
        history.replace(AppRouter.ADD_ORG.path);
        return;
      }

      if (!organization) {
        return;
      }

      const isSeamlessOnboardingFlowEnabled =
        await getAdHocSettingOrDefinitionValue(
          FEATURE_FLAGS_NAMES.isSeamlessOnboardingFlowEnabled
        );

      // if seamless onboarding flow is enabled, we don't need to add provider, just redirect to root
      if (isSeamlessOnboardingFlowEnabled) {
        history.replace(AppRouter.ROOT.path);
        return;
      }

      if (!gitSensorAuthorizations.length && !account.extra) {
        history.replace(AppRouter.PERSONAL_IDENTIFICATION_FORM.path);
        return;
      }

      if (!gitSensorAuthorizations.length || location?.state?.error) {
        history.replace(AppRouter.ADD_PROVIDER.path, location?.state);
        return;
      }

      if (isPostOnboardingWithoutRepos) {
        history.replace(`${AppRouter.REPOSITORIES.path}?isOrgSettings=true`);
        return;
      }

      const userSavedReposTimeStamp = BrowserStorageAccessor().local.get(
        LOCAL_STORAGE_KEYS.USER_SAVED_REPOS_TS
      );

      if (userSavedReposTimeStamp && !repositories.length) {
        history.replace(AppRouter.PROCESSING.path);
        return;
      }

      if (!providerOrganizationsVerified) {
        history.replace(AppRouter.VERIFY_PROVIDER.path);
        return;
      }

      if (!repositories.length) {
        history.replace(AppRouter.ADD_REPOS.path);
        return;
      }

      if (repositories.length) {
        history.push(AppRouter.ROOT.path);
      }
    };

    handleOnboarding();
  }, [
    account,
    organization,
    gitSensorAuthorizations,
    repositories,
    jobs.RepositoriesCollector,
    providerOrganizationsVerified
  ]);

  return (
    <>
      {organization && (
        <TopBar
          showDatePicker={false}
          isClickableLogo={false}
          isOnTopOfModals
          isOnboarding
        />
      )}
      <Loader />
      <Switch>
        <Route path={AppRouter.ADD_ORG.path} component={AddOrganization} />
        <Route
          path={AppRouter.ADD_PROVIDER.path}
          component={isTeamLicense ? ProviderSetupV2 : AddProvider}
        />
        <Route
          path={AppRouter.VERIFY_PROVIDER.path}
          component={VerifyProvider}
        />
        <Route path={AppRouter.ADD_REPOS.path} component={AddRepositories} />
        <Route path={AppRouter.PROCESSING.path} component={Processing} />
      </Switch>
    </>
  );
};
export default Onboarding;
